import React, { useEffect, useState, useRef } from "react";
import ErrorMessage from "../../../../../Shared/error-message/errorMessage";
import Styles from "../../Shared/sharedStyles.module.scss";
import InputField from "../../Shared/inputField/inputField";
import {
  buttonStyles,
  StyledButton,
} from "../../../../../Shared/button/button";
import {
  validBankAccountNumber,
  validIFSCNumber,
  validNameCheck,
} from "../../../../../utils/validation";
import { IdsGenerator } from "../../../../../utils/idGenerator";

function BankAccountVerificationAdvancePopup({ handleResponse, name }: any) {
  const errorObject = {
    accountNumberError: false,
    ifscNumberError: false,
    nameToMatchError: false,
    consentError: false,
    nameEmptyError: false,
  };

  const [error, setError] = useState({ ...errorObject });
  const [isChecked, setIsChecked] = useState(false);
  const [isNameMatchEnabled, setIsNameMatchEnabled] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pennyDropChecked, setPennyDropChecked] = useState(true);

  const accountNumberRef = useRef<any>("");
  const ifscNumberRef = useRef<any>("");
  const nameMatchRef = useRef<any>("");

  function isValid() {
    if (isNameMatchEnabled) {
      if (
        accountNumberRef.current.value &&
        ifscNumberRef.current.value &&
        nameMatchRef.current.value &&
        Object.values(error).every((value) => value === false)
      ) {
        return setIsDisabled(false);
      }
    }

    if (!isNameMatchEnabled) {
      if (
        accountNumberRef.current.value &&
        ifscNumberRef.current.value &&
        Object.values(error).every((value) => value === false)
      ) {
        return setIsDisabled(false);
      }
    }

    return setIsDisabled(true);
  }

  useEffect(() => {
    if (isNameMatchEnabled) {
      setIsDisabled(true);
    }
    if (!isNameMatchEnabled) {
      setError(() => ({
        ...error,
        nameToMatchError: false,
      }));
      if (accountNumberRef && ifscNumberRef) {
        setIsDisabled(false);
      }
    }
  }, [isNameMatchEnabled]);

  useEffect(() => {
    isValid();
  }, [
    accountNumberRef,
    ifscNumberRef,
    nameMatchRef,
    isNameMatchEnabled,
    error,
  ]);

  const handleChange = (event: any) => {
    setPennyDropChecked(event.target.value === "true");
  };

  function checkAccountNumberValidation() {
    if (
      !validBankAccountNumber(
        accountNumberRef.current.value.trim().toUpperCase()
      )
    ) {
      setError(() => ({
        ...error,
        accountNumberError: true,
      }));
    } else {
      setError(() => ({
        ...error,
        accountNumberError: false,
      }));
    }
  }

  function checkIfscNumberValidation() {
    if (!validIFSCNumber(ifscNumberRef.current.value.trim().toUpperCase())) {
      setError(() => ({
        ...error,
        ifscNumberError: true,
      }));
    } else {
      setError(() => ({
        ...error,
        ifscNumberError: false,
      }));
    }
  }

  function isValidNameMatch() {
    setError({ ...errorObject });
    if (
      validBankAccountNumber(accountNumberRef.current.value.trim()) &&
      validIFSCNumber(ifscNumberRef.current.value.trim().toUpperCase()) &&
      validNameCheck(nameMatchRef.current.value)
    ) {
      setError(errorObject);
      return setIsDisabled(false);
    }
    if (!validNameCheck(nameMatchRef.current.value)) {
      setError(() => ({
        ...error,
        nameToMatchError: true,
      }));
    }
    return setIsDisabled(true);
  }

  function submitVerificationRequest() {
    const accountNumber = accountNumberRef.current.value.toUpperCase();
    const ifscNumber = ifscNumberRef.current.value.toUpperCase();
    const name_to_match = nameMatchRef.current?.value;
    const is_penny_drop = pennyDropChecked;

    const consent = isChecked === true ? "Y" : "N";

    if (!isChecked) {
      setError(() => ({
        ...error,
        consentError: true,
      }));
      return false;
    }
    setLoading(true);
    handleResponse(
      { accountNumber, ifscNumber, name_to_match, consent, is_penny_drop },
      setLoading
    );
  }

  return (
    <div className={`${Styles.content}`}>
      <div className={`${Styles.infoForm} pl-2`}>
        <hr />
        <p className="mt-2">Fill in the below details to run verification</p>
        <div className="d-flex flex-column">
          <div className="d-flex flex-column mb-2">
            <label className={`${Styles.label} flex-grow-1`}>
              Account Number
            </label>
            <InputField
              maxLength={16}
              id={IdsGenerator({
                prefix: "bank account verification advance",
                id: "change",
                suffix: "account number",
              })}
              inputRef={accountNumberRef}
              onChange={checkAccountNumberValidation}
              isError={error.accountNumberError}
              purpose="forBankVerification"
            />
            {error.accountNumberError && (
              <ErrorMessage>Please enter a valid Account number</ErrorMessage>
            )}
          </div>
          <div className="d-flex flex-column mb-3 ">
            <label className={`${Styles.label} flex-grow-1`}>IFSC Number</label>
            <InputField
              id={IdsGenerator({
                prefix: "bank account verification advance",
                id: "change",
                suffix: "ifsc number",
              })}
              onChange={checkIfscNumberValidation}
              maxLength={11}
              inputRef={ifscNumberRef}
              isError={error.ifscNumberError}
              purpose="forBankVerification"
            />
            {error.ifscNumberError && (
              <ErrorMessage>Please enter a valid IFSC number</ErrorMessage>
            )}
          </div>
          <div className="pl-4 pb-2 d-flex flex-column">
            <div className="d-flex align-items-center py-0 gap-2">
              <input
                id={IdsGenerator({
                  prefix: "bank account verification advance",
                  id: "check",
                  suffix: "is name match",
                })}
                data-testid={IdsGenerator({
                  prefix: "bank account verification advance",
                  id: "check",
                  suffix: "is name match",
                })}
                type="checkbox"
                className={`${Styles.formCheckInput} `}
                onChange={(e: any) => setIsNameMatchEnabled(e.target.checked)}
              />
              <p className={`${Styles.label} mb-0`}>Is name match enabled?</p>
            </div>
          </div>
          {isNameMatchEnabled && (
            <div className="d-flex flex-column mb-2 ">
              <label className={`${Styles.label} flex-grow-1`}>
                Name To Match
              </label>
              <InputField
                id={IdsGenerator({
                  prefix: "bank account verification advance",
                  id: "change",
                  suffix: "is name match",
                })}
                onChange={isValidNameMatch}
                inputRef={nameMatchRef}
                isError={error.nameToMatchError}
                purpose="forBankVerification"
                text="lowerCase"
              />
              {error.nameToMatchError && (
                <ErrorMessage>Please enter a valid Name</ErrorMessage>
              )}
            </div>
          )}
          <>
            <label className={`${Styles.label} flex-grow-1 mt-2`}>
              Penny Drop
            </label>
            <div className="d-flex mt-1 gap-4">
              <label className="mr-2 mb-0">
                <input
                  id={IdsGenerator({
                    prefix: "bank account verification advance",
                    id: "select true",
                    suffix: "penny drop",
                  })}
                  data-testid={IdsGenerator({
                    prefix: "bank account verification advance",
                    id: "select true",
                    suffix: "penny drop",
                  })}
                  type="radio"
                  value="true"
                  checked={pennyDropChecked === true}
                  onChange={handleChange}
                  className="mr-1"
                  style={{ cursor: "pointer" }}
                />
                True
              </label>

              <label className="mb-0">
                <input
                  id={IdsGenerator({
                    prefix: "bank account verification advance",
                    id: "select false",
                    suffix: "penny drop",
                  })}
                  data-testid={IdsGenerator({
                    prefix: "bank account verification advance",
                    id: "select false",
                    suffix: "penny drop",
                  })}
                  type="radio"
                  value="false"
                  checked={pennyDropChecked === false}
                  onChange={handleChange}
                  className="mr-1"
                  style={{ cursor: "pointer" }}
                />
                False
              </label>
            </div>
          </>
          <div className="pt-4 pl-4 pb-4 mt-4 d-flex flex-column">
            <div className="d-flex align-items-center py-0 gap-2">
              <input
                id={IdsGenerator({
                  prefix: "bank account verification advance",
                  id: "check",
                  suffix: "consent",
                })}
                data-testid={IdsGenerator({
                  prefix: "bank account verification advance",
                  id: "check",
                  suffix: "consent",
                })}
                type="checkbox"
                className={`${Styles.formCheckInput} `}
                onChange={(e: any) => setIsChecked(e.target.checked)}
              />
              <p className={`${Styles.ackText} mb-0`}>
                I hereby agree, to let zoop.one verify my data for verification
              </p>
            </div>
            {error.consentError && (
              <ErrorMessage>Please provide consent!</ErrorMessage>
            )}
          </div>
          <div className={`${Styles.buttonGroup}`}>
            <StyledButton
              id={IdsGenerator({
                prefix: "bank account verification advance",
                id: "click",
                suffix: "run verification",
              })}
              hoverStyle={{ ...buttonStyles.primary.hover }}
              disabled={isDisabled}
              isLoading={loading}
              style={{ ...buttonStyles.primary.normal }}
              onClick={() => {
                setTimeout(() => setError({ ...errorObject }), 2000);
                submitVerificationRequest();
              }}
            >
              Run Verification
            </StyledButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BankAccountVerificationAdvancePopup;
